import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Link,
  // Navigation,
  // NavigationItem,
} from '@americanexpress/dls-react';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { getLocaleData } from '@/selectors/localeSelectors';
import { useRouteParams } from '@/context/routeParamsContext';
import { cn } from '@/helpers/utils';
import { Card } from '@/constants';
import { useAnalyticsContext } from '@/context/analyticsContext';

import { useModal } from '@/context/modalContext';
import LFLink from './LFLink';
import { modalTypes } from './modals/constants';

const Footer = ({ className }) => {
  const { customTrack } = useAnalyticsContext();
  const { setModalName } = useModal();
  const { product, loungeId } = useRouteParams();
  const { locale } = useSelector(getLocaleData);

  const {
    footer: { additionalInfo, popularAirports },
  } = useCommonContent();

  const handleLinkClick = (event, link) => {
    event.preventDefault();
    setModalName(modalTypes.TERMS_MODAL);
    customTrack({ event, element: link.element, detail: link.label });
  };

  // The popular airports list will ONLY apply for the US Platinum and Centurion for now, since not all LF versions have the same airports included.
  const isUSPlatinumOrCenturion =
    locale === 'en-US' &&
    (product === Card.Platinum || product === Card.Centurion);

  // DO NOT display Additional Info link when on the Lounge details page
  const isLoungeDetailsPage = !!loungeId;

  const footerLinks = [
    {
      // the additional information section will be displayed for all cards
      display: !isLoungeDetailsPage,
      heading: additionalInfo?.header,
      links: [
        {
          element: 'terms and conditions link',
          label: additionalInfo?.termsLabel,
        },
      ].map((link) => ({
        ...link,
        // add link.label check for now to avoid extra link when switching countries
        label: link.label && (
          <li key={link.label} className="tw-mr-5dot5 tw-inline">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- link to open the additional information modals */}
            <Link
              className="tw-py-0 tw-text-link"
              onClick={(e) => handleLinkClick(e, link)}
            >
              {link.label}
            </Link>
          </li>
        ),
      })),
    },
    {
      display: isUSPlatinumOrCenturion,
      heading: popularAirports?.header,
      links: popularAirports?.links?.map((link, i) => ({
        ...link,
        label: (
          <Fragment key={link.iata}>
            {i > 0 && <li className="tw-mx-2 tw-inline">|</li>}
            <li className="tw-inline">
              <LFLink
                key={`popular-airport-${link.iata}`}
                detail={`popular-airport-${link.iata}`}
                className="tw-text-link hover_tw-underline"
                to={`${product}/${link.iata}`}
                element="footer"
              >
                {link.label}
              </LFLink>
            </li>
          </Fragment>
        ),
      })),
    },
  ];

  // do not display the footer on the lounge details page for non US Platinum & Centurion cards
  if (isLoungeDetailsPage && !isUSPlatinumOrCenturion) {
    return null;
  }

  return (
    <section
      className={cn(
        'tw-mx-auto tw-mb-10 tw-max-w-screen-xl tw-px-4 md_tw-px-6 lg_tw-px-8 xl_tw-px-10',
        className
      )}
    >
      <hr className="tw-my-10 tw-border-t tw-border-gray-300" />
      {footerLinks
        .filter((link) => link.display)
        .map(({ heading, links }) => (
          <div key={heading} className="tw-mb-4">
            <h3
              id={heading.replaceAll(' ', '-').toLowerCase()}
              className="tw-heading-xs-bold tw-mb-2 tw-text-gray-700"
            >
              {heading}
            </h3>
            <ul>{links.map((link) => link.label)}</ul>
          </div>
        ))}

      {/* uncomment after myca footer is updated to dls7 */}
      {/* <Navigation className="sm_tw-hidden">
          {footerLinks.map(({ heading, links }) => (
            <NavigationItem
              key={heading}
              label={
                <div className="tw-text-15px tw-font-normal">{heading}</div>
              }
            >
              {links.map((link) => (
                <NavigationItem
                  key={link.iata}
                  label={
                    <LFLink
                      to={`${product}/${link.iata}`}
                    >
                      {link.label}
                      <ChevronLeft className="tw-ml-1 tw-inline tw-h-3 tw-w-3 tw-rotate-180" />
                    </LFLink>
                  }
                />
              ))}
            </NavigationItem>
          ))}
        </Navigation> */}
    </section>
  );
};

Footer.propTypes = {
  className: PropTypes.string,
};

export default Footer;
